import { useAnalytics } from '@repo/analytics/analytics-provider';
import { Button, IconButton } from '@repo/design-system/components/ds/button';
import { Icon } from '@repo/design-system/components/ds/icon';
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { DropdownMenu, DropdownMenuItem } from '@repo/design-system/components/ui/dropdown-menu';
import { TextArea } from '@repo/design-system/components/ui/text-area';
import { TextInput } from '@repo/design-system/components/ui/text-input';
import { toast } from '@repo/design-system/components/ui/toaster';
import { Type } from '@repo/design-system/components/ui/type';
import { useHostType } from '@repo/host/hooks/use-host-info';
import { useT } from '@repo/i18n/components/i18n.client';
import { encodeSharedChecklist } from '@repo/map-user-data/share';
import { SHARE_STATE_SCHEMA_VERSION } from '@repo/map-user-data/share-state';
import { DESCRIPTION_MAX_LENGTH, TITLE_MAX_LENGTH } from '@repo/map-user-data/types';
import { css } from '@repo/panda/css';
import { useDeleteSavedChecklistMutation, useListSavedChecklistsQuery, useUpdateSavedChecklistMutation } from '@repo/store/maps/api/mapsApi';
import { useAppDispatch, useAppSelector } from '@repo/store/maps/hooks';
import { selectAuthToken } from '@repo/store/maps/slices/auth';
import { selectActiveCustomChecklistId, setActiveCustomChecklistId, setSidebarView } from '@repo/store/maps/slices/clientContext';
import { selectCurrentMapInfo, selectExternalMapId, selectMapData, selectSteamId, selectTitleName, selectTitleSlug } from '@repo/store/maps/slices/mapData';
import type { MapDataLocationFeature } from '@repo/types/maps/map-types';
import { createPublicMapUrl } from '@repo/types/maps/map-utils';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { createShareState, createShareStateUrl } from '../utils/share-state-client';
import { checklistHeroIconClass, useChecklistHero } from './checklist-hero';
import { DeleteChecklistConfirmModal } from './delete-checklist-confirm-modal';
import { PinListSidebarShell } from './pin-list-sidebar-shell';
interface CustomChecklistSidebarViewProps {
  onBack: () => void;
  isMobilePane?: boolean;
  onSearchFocus?: () => void;
}
export const CustomChecklistSidebarView: React.FC<CustomChecklistSidebarViewProps> = ({
  onBack,
  isMobilePane = false,
  onSearchFocus
}) => {
  const {
    t
  } = useT(['game_map']);
  const mapData = useAppSelector(selectMapData);
  const externalMapId = useAppSelector(selectExternalMapId);
  const titleSlug = useAppSelector(selectTitleSlug);
  const titleName = useAppSelector(selectTitleName);
  const currentMapInfo = useAppSelector(selectCurrentMapInfo);
  const mapName = mapData?.metadata.name ?? currentMapInfo?.name;
  const steamId = useAppSelector(selectSteamId);
  const activeChecklistId = useAppSelector(selectActiveCustomChecklistId);
  const isLoggedIn = useAppSelector(state => state.auth.isAuthenticated);
  const token = useAppSelector(selectAuthToken);
  const dispatch = useAppDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editPinIds, setEditPinIds] = useState<string[]>([]);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const {
    data: savedChecklists,
    isFetching: isFetchingSavedChecklists
  } = useListSavedChecklistsQuery({
    mapExternalId: externalMapId ?? '',
    token: token ?? ''
  }, {
    skip: !isLoggedIn || !externalMapId || !token
  });
  const checklist = useMemo(() => savedChecklists?.find(c => c.id === activeChecklistId) ?? null, [savedChecklists, activeChecklistId]);
  const [updateChecklist, {
    isLoading: isUpdating
  }] = useUpdateSavedChecklistMutation();
  const [deleteChecklist, {
    isLoading: isDeleting
  }] = useDeleteSavedChecklistMutation();
  const analytics = useAnalytics();
  const hostType = useHostType();
  useEffect(() => {
    if (savedChecklists && !checklist && !isFetchingSavedChecklists) {
      onBack();
    }
  }, [savedChecklists, checklist, isFetchingSavedChecklists, onBack]);
  const effectivePinIds = isEditing ? editPinIds : checklist?.pinIds ?? [];
  const pinFeatures = useMemo<MapDataLocationFeature[]>(() => {
    if (!mapData) return [];
    const wanted = new Set(effectivePinIds);
    return mapData.geojson.features.filter((feature): feature is MapDataLocationFeature => feature.geometry.type === 'Point' && wanted.has(feature.properties.externalId));
  }, [effectivePinIds, mapData]);
  const {
    backgroundImageUrl,
    pinColor,
    iconChar,
    iconFontStyle
  } = useChecklistHero(pinFeatures, mapData, steamId);
  const enterEditMode = () => {
    if (!checklist) return;
    setEditTitle(checklist.title);
    setEditDescription(checklist.description);
    setEditPinIds([...checklist.pinIds]);
    setIsEditing(true);
  };
  const cancelEdit = () => {
    setIsEditing(false);
  };
  const saveEdit = async () => {
    if (!checklist || !externalMapId || !token) return;
    const trimmedTitle = editTitle.trim();
    if (trimmedTitle.length === 0) {
      toast({
        description: t($ => $.save_checklist.give_it_a_name),
        variant: 'error'
      });
      return;
    }
    if (trimmedTitle.length > TITLE_MAX_LENGTH) {
      toast({
        description: t($_0 => $_0.save_checklist.title_must_be_100_characters_or_fewer),
        variant: 'error'
      });
      return;
    }
    if (editDescription.length > DESCRIPTION_MAX_LENGTH) {
      toast({
        description: t($_1 => $_1.save_checklist.description_must_be_500_characters_or_fewer),
        variant: 'error'
      });
      return;
    }
    try {
      await updateChecklist({
        mapExternalId: externalMapId,
        id: checklist.id,
        input: {
          title: trimmedTitle,
          description: editDescription,
          pinIds: editPinIds
        },
        token
      }).unwrap();
      if (analytics && hostType) {
        analytics.event('game_map_saved_checklist_update', {
          mapId: externalMapId,
          mapName,
          titleName,
          checklistId: checklist.id,
          pinsRemoved: checklist.pinIds.length - editPinIds.length,
          titleChanged: trimmedTitle !== checklist.title,
          descriptionChanged: editDescription !== checklist.description,
          platform: hostType
        });
      }
      toast({
        description: t($_3 => $_3.save_checklist.checklist_saved)
      });
      setIsEditing(false);
    } catch {
      toast({
        description: t($_2 => $_2.save_checklist.couldnt_save_checklist),
        variant: 'error'
      });
    }
  };
  const handleRemovePin = (itemId: string) => {
    setEditPinIds(ids => ids.filter(id => id !== itemId));
  };
  const handleShare = async () => {
    if (!checklist) return;
    try {
      const mapSlug = currentMapInfo?.slug;
      const baseShareUrl = titleSlug && mapSlug ? createPublicMapUrl({
        titleSlug,
        mapSlug
      }) : externalMapId ? `${window.location.origin}/maps/${externalMapId}` : null;
      if (!baseShareUrl) throw new Error('unable to construct share URL');
      let shareUrl: string | null = null;
      if (externalMapId) {
        try {
          const token_0 = await createShareState({
            version: SHARE_STATE_SCHEMA_VERSION,
            kind: 'sharedChecklist',
            mapExternalId: externalMapId,
            title: checklist.title,
            description: checklist.description,
            pinIds: checklist.pinIds
          });
          shareUrl = createShareStateUrl(baseShareUrl, token_0);
        } catch {
          shareUrl = null;
        }
      }
      if (!shareUrl) {
        const encoded = await encodeSharedChecklist({
          title: checklist.title,
          description: checklist.description,
          pinIds: checklist.pinIds
        });
        const fallbackUrl = new URL(baseShareUrl);
        fallbackUrl.searchParams.set('cl', encoded);
        shareUrl = fallbackUrl.toString();
      }
      await navigator.clipboard.writeText(shareUrl);
      toast({
        description: t($_5 => $_5.save_checklist.share_link_copied)
      });
      if (analytics && hostType && externalMapId) {
        analytics.event('game_map_saved_checklist_share_copy', {
          mapId: externalMapId,
          mapName,
          titleName,
          checklistId: checklist.id,
          pinCount: checklist.pinIds.length,
          platform: hostType
        });
      }
    } catch {
      toast({
        description: t($_4 => $_4.save_checklist.couldnt_copy_share_link),
        variant: 'error'
      });
    }
  };
  const handleConfirmDelete = async () => {
    if (!checklist || !externalMapId || !token) return;
    try {
      await deleteChecklist({
        mapExternalId: externalMapId,
        id: checklist.id,
        token
      }).unwrap();
      if (analytics && hostType) {
        analytics.event('game_map_saved_checklist_delete', {
          mapId: externalMapId,
          mapName,
          titleName,
          checklistId: checklist.id,
          trigger: 'menu',
          platform: hostType
        });
      }
      toast({
        description: t($_7 => $_7.save_checklist.checklist_deleted)
      });
      setIsDeleteConfirmOpen(false);
      dispatch(setActiveCustomChecklistId(null));
      dispatch(setSidebarView('checklist'));
    } catch {
      toast({
        description: t($_6 => $_6.save_checklist.couldnt_delete_checklist),
        variant: 'error'
      });
    }
  };
  if (!checklist) return null;
  const viewModeActions = <FlexRow gap="8" align="center">
            <IconButton onClick={handleShare} color="primary" size="md" aria-label={t($_8 => $_8.save_checklist.share)}>
                <Icon icon="ios_share" decorative />
            </IconButton>
            <DropdownMenu placement="bottom-end" trigger={<IconButton color="secondary" size="md" aria-label={t($_9 => $_9.save_checklist.list_options)}>
                        <Icon icon="more_vert" decorative />
                    </IconButton>}>
                <DropdownMenuItem value="edit" leadingIcon="edit" onSelect={enterEditMode}>
                    {t($_10 => $_10.save_checklist.edit_list)}
                </DropdownMenuItem>
                <DropdownMenuItem value="delete" leadingIcon="delete" destructive onSelect={() => setIsDeleteConfirmOpen(true)}>
                    {t($_11 => $_11.save_checklist.delete_list)}
                </DropdownMenuItem>
            </DropdownMenu>
        </FlexRow>;
  const editModeActions = <FlexRow gap="8" align="center">
            <Button onClick={cancelEdit} color="secondary" size="md" disabled={isUpdating}>
                {t($_12 => $_12.save_checklist.cancel)}
            </Button>
            <Button onClick={saveEdit} color="primary" size="md" disabled={isUpdating}>
                {t($_13 => $_13.save_checklist.save)}
            </Button>
        </FlexRow>;
  const sidebarHeading = isEditing ? editTitle : checklist.title;
  const sidebarDescription = isEditing ? editDescription : checklist.description;
  const hero = <FlexColumn align="center" gap="12" width="full" data-drawer-drag="true">
            <div className={checklistHeroIconClass} style={{
      backgroundColor: pinColor,
      ...iconFontStyle
    }}>
                {iconChar}
            </div>
            {isEditing ? <FlexColumn gap="8" width="full">
                    <TextInput fullWidth value={editTitle} onChange={e => setEditTitle(e.target.value)} placeholder={t($_14 => $_14.save_checklist.title)} maxLength={TITLE_MAX_LENGTH} aria-label={t($_15 => $_15.save_checklist.title)} />
                    <TextArea fullWidth value={editDescription} onChange={e_0 => setEditDescription(e_0.target.value)} placeholder={t($_16 => $_16.save_checklist.optional_notes)} maxLength={DESCRIPTION_MAX_LENGTH} aria-label={t($_17 => $_17.save_checklist.description)} rows={3} />
                </FlexColumn> : <FlexColumn align="center" gap="4" width="full">
                    <Type variant="header-m" color="white" className={css({
        textAlign: 'center',
        wordBreak: 'break-word'
      })} title={sidebarHeading}>
                        {sidebarHeading}
                    </Type>
                    {sidebarDescription && <Type variant="body-s" color="text.secondary" className={css({
        textAlign: 'center',
        wordBreak: 'break-word'
      })}>
                            {sidebarDescription}
                        </Type>}
                </FlexColumn>}
        </FlexColumn>;
  return <>
            <PinListSidebarShell backgroundImageUrl={backgroundImageUrl} isMobilePane={isMobilePane} onBack={onBack} onSearchFocus={onSearchFocus} headerAction={isEditing ? editModeActions : viewModeActions} pinFeatures={pinFeatures} hero={hero} disableSearch={isEditing} editMode={isEditing} onRemovePin={handleRemovePin} removeLabel={t($_18 => $_18.save_checklist.remove_from_list)} feedbackSource="custom-checklist-list" feedbackAnchorLocationId={checklist.id} selectSource="sidebar_checklist" boundsFitKey={checklist.id} />
            <DeleteChecklistConfirmModal isOpen={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)} onConfirm={handleConfirmDelete} isDeleting={isDeleting} />
        </>;
};